import { useCallback, useEffect, useRef, useState } from 'react';
import { classifyPortalError } from '../utils/apiError.js';

// Same loading/retry contract as the Dashboard cards: silent auto-retry up to
// 3 times, then a manual "Retry" button capped at 3 clicks total. On final
// failure the error is classified (404/network-down -> generic "unavailable"
// message, GlobalError ENTITY_NOT_FOUND_ERROR -> its own message); every
// other error leaves `errorMessage` null so callers fall back to their own
// existing default text.
export function useRetryFetch(fetchFn, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [errorInfo, setErrorInfo] = useState(null);
  const [manualRetries, setManualRetries] = useState(3);
  const retryCountRef = useRef(0);
  const mountedRef = useRef(true);
  const fetchFnRef = useRef(fetchFn);
  fetchFnRef.current = fetchFn;

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const fetchData = useCallback(() => {
    setLoading(true);
    setError(false);
    setErrorInfo(null);
    setData(null);
    retryCountRef.current = 0;

    const attempt = () => {
      fetchFnRef.current()
        .then((result) => {
          if (mountedRef.current) {
            setData(result);
            setLoading(false);
          }
        })
        .catch((err) => {
          if (!mountedRef.current) return;
          const isNotFound = err?.response?.status === 404;
          if (!isNotFound && retryCountRef.current < 3) {
            retryCountRef.current += 1;
            attempt();
          } else {
            setError(true);
            setErrorInfo(classifyPortalError(err));
            setLoading(false);
          }
        });
    };

    attempt();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const retry = useCallback(() => {
    setManualRetries((c) => c - 1);
    fetchData();
  }, [fetchData]);

  return {
    data,
    setData,
    loading,
    error,
    errorMessage: errorInfo?.message ?? null,
    isEntityNotFound: errorInfo?.isEntityNotFound ?? false,
    manualRetries,
    retry,
    refetch: fetchData,
  };
}
